import { useState } from "react";

import { Card } from "../Card";
import { FormField, inputStyle } from "../ui/FormField";
import { StageBadge } from "./ConsentAttestationField";
import type { StageTone } from "./ConsentAttestationField";
import type { EngagementRecord } from "../../api/engagements";
import type { DrillStage, ScriptStage } from "../../lib/videoDrill";
import { mapVideoError } from "../../lib/videoDrill";
import { ApiError } from "../../lib/api";
import { useUpdateEngagement } from "../../hooks/useEngagement";
import { useApproveScript } from "../../hooks/useVideo";

// ─── Button styles (local, consistent with DispatchCallModal) ─────────────────

const btnBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: "var(--radius-input)",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 14px",
  minHeight: 36,
  border: "1px solid transparent",
};

const primaryBtn: React.CSSProperties = {
  ...btnBase,
  background: "var(--accent)",
  color: "#FFFBF4",
  borderColor: "var(--accent)",
};

const primaryBtnDisabled: React.CSSProperties = {
  ...btnBase,
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

const ghostBtn: React.CSSProperties = {
  ...btnBase,
  background: "transparent",
  color: "var(--text-secondary)",
  borderColor: "var(--border)",
};

const ghostBtnDisabled: React.CSSProperties = {
  ...ghostBtn,
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

// ─── Script stage → badge mapping ────────────────────────────────────────────

interface StageMeta {
  label: string;
  tone: StageTone;
}

const SCRIPT_STAGE_META: Record<ScriptStage, StageMeta> = {
  none: { label: "No script", tone: "muted" },
  draft: { label: "Draft", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  edited: { label: "Edited — re-approve", tone: "warning" },
};

// ─── VideoConfigCard ──────────────────────────────────────────────────────────

interface Props {
  engagementId: string;
  rec: EngagementRecord;
  stage: DrillStage;
}

export function VideoConfigCard({ engagementId, rec, stage }: Props) {
  const vc = rec.video_config;

  // New video engagements default to the Photo Avatar (talking_photo) path —
  // the dominant shape for a consented-exec drill (one photo of the person).
  // An existing config keeps whatever it was saved with.
  const [avatarType, setAvatarType] = useState<"avatar" | "talking_photo">(
    vc?.avatar_type ?? "talking_photo",
  );
  const [avatarId, setAvatarId] = useState(vc?.avatar_id ?? "");
  const [voiceId, setVoiceId] = useState(vc?.voice_id ?? "");
  const [script, setScript] = useState(vc?.script ?? "");
  const [err, setErr] = useState<string | null>(null);

  const update = useUpdateEngagement(engagementId);
  const approve = useApproveScript(engagementId);

  function save() {
    setErr(null);
    update.mutate(
      {
        video_config: {
          avatar_interaction_mode: "pre_rendered",
          avatar_provider: "heygen",
          avatar_type: avatarType,
          avatar_id: avatarId,
          voice_id: voiceId,
          script: script || null,
          script_approved_at: vc?.script_approved_at ?? null,
          script_approved_by: vc?.script_approved_by ?? null,
          approved_script_sha256: vc?.approved_script_sha256 ?? null,
        },
      },
      {
        onError: (e) => {
          setErr(e instanceof ApiError ? mapVideoError(e.detail) : "Save failed");
        },
      },
    );
  }

  function doApprove() {
    setErr(null);
    approve.mutate(undefined, {
      onError: (e) => {
        setErr(e instanceof ApiError ? mapVideoError(e.detail) : "Approve failed");
      },
    });
  }

  const stageMeta = SCRIPT_STAGE_META[stage.script];
  const approveDisabled =
    !script.trim() || stage.script === "approved" || approve.isPending;

  return (
    <Card title="">
      {/* Custom header row with title + badge */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 12,
          borderBottom: "1px solid var(--border-subtle)",
          marginBottom: -2,
        }}
      >
        <h5
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            color: "var(--text-primary)",
          }}
        >
          Script &amp; avatar
        </h5>
        <StageBadge label={stageMeta.label} tone={stageMeta.tone} />
      </div>

      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <FormField label="Avatar type">
          <select
            style={inputStyle}
            value={avatarType}
            onChange={(e) => setAvatarType(e.target.value as "avatar" | "talking_photo")}
          >
            <option value="talking_photo">Photo Avatar / Avatar IV (from a still)</option>
            <option value="avatar">Classic Avatar (from video footage)</option>
          </select>
        </FormField>

        <FormField label={avatarType === "talking_photo" ? "Photo Avatar ID" : "Avatar ID"}>
          <input
            style={inputStyle}
            value={avatarId}
            placeholder={
              avatarType === "talking_photo"
                ? "HeyGen Photo Avatar / talking_photo ID"
                : "HeyGen avatar ID"
            }
            onChange={(e) => setAvatarId(e.target.value)}
          />
          <p
            style={{
              margin: "6px 0 0",
              fontSize: 10.5,
              lineHeight: 1.5,
              color: "var(--text-muted)",
              fontFamily: "var(--font-sans)",
            }}
          >
            {avatarType === "talking_photo"
              ? "HeyGen → Avatars → open your Photo Avatar → the look/group ID (32-char hex). Custom consented avatars are almost always this type."
              : "HeyGen → Avatars → a classic avatar built from recorded video footage."}
          </p>
        </FormField>

        <FormField label="Voice ID">
          <input
            style={inputStyle}
            value={voiceId}
            placeholder="HeyGen voice ID"
            onChange={(e) => setVoiceId(e.target.value)}
          />
        </FormField>

        <FormField label="Script">
          <textarea
            style={{ ...inputStyle, minHeight: 90, resize: "vertical", width: "100%", boxSizing: "border-box" }}
            value={script}
            placeholder="Enter the deepfake script…"
            onChange={(e) => setScript(e.target.value)}
          />
        </FormField>

        <div
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
          }}
        >
          Mode: pre-rendered
        </div>

        {/* Error */}
        {err ? (
          <div style={{ color: "var(--error)", fontSize: 12, fontFamily: "var(--font-sans)" }}>
            {err}
          </div>
        ) : null}

        {/* Footer actions */}
        <div
          style={{
            display: "flex",
            gap: 8,
            paddingTop: 12,
            borderTop: "1px solid var(--border-subtle)",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={save}
            disabled={update.isPending}
            style={update.isPending ? primaryBtnDisabled : primaryBtn}
          >
            {update.isPending ? "SAVING…" : "SAVE"}
          </button>
          <button
            onClick={doApprove}
            disabled={approveDisabled}
            style={approveDisabled ? ghostBtnDisabled : ghostBtn}
          >
            {approve.isPending ? "APPROVING…" : "APPROVE SCRIPT"}
          </button>
        </div>
      </div>
    </Card>
  );
}
